import React, { Component } from 'react';

import CodeEditor from 'app/developer-v3/components/CodeEditor';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import { getActionUrl, getDynamicInput } from 'app/developer-v3/utils';

import type {
  ActionInputParams,
  ActionInputType,
} from 'app/developer-v3/types';
import type { StringMap } from 'app/common/types';

type Props = {
  action: any;
  isReadOnly: boolean;
  isSaving: boolean;
  onFormChange: (values: StringMap) => void;
  params: ActionInputParams;
  resetUnsavedEdits: () => void;
  saveActionChanges: (skipConfirm: boolean) => void;
  setInputField: (key: string, type: ActionInputType) => void;
};

type State = {
  attemptedSave: boolean;
  error: string;
};

class ActionDynamicInputEditor extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    props.setInputField(props.params.inputKey, 'dynamic');

    this.state = {
      attemptedSave: false,
      error: '',
    };
  }

  get isSaveDisabled() {
    const { attemptedSave, error } = this.state;
    if (this.props.isReadOnly) return true;

    if (!attemptedSave) return false;

    return !!error;
  }

  get dynamicInput() {
    const { action, params } = this.props;
    return getDynamicInput(action, params.inputKey);
  }

  get activeError() {
    const { error, attemptedSave } = this.state;

    return attemptedSave ? error : '';
  }

  get helpText() {
    return (
      <React.Fragment>
        Use Dynamic Fields to fetch user defined fields with a custom API calls
        to your app. Zapier expects the response data to be an array containing
        field keys and names.{' '}
        <a
          href="https://docs.zapier.com/platform/build/dynamic-field"
          target="_blank"
        >
          Learn more
        </a>
        .
      </React.Fragment>
    );
  }

  componentWillUnmount() {
    this.props.resetUnsavedEdits();
  }

  isValid = (input: string = this.dynamicInput) => {
    return input.includes('return');
  };

  onInputChange = (value: string = '') => {
    this.props.onFormChange({ source: value });

    const error = this.isValid(value)
      ? ''
      : 'Function requires a returned value.';

    this.setState({ error });
  };

  onSaveField = () => {
    if (this.isValid()) {
      this.props.saveActionChanges(true);
    }

    this.setState({ attemptedSave: true });
  };

  render() {
    const { props } = this;

    return (
      <EditorBodyLayout
        cancelRoute={`${getActionUrl(props.params)}/input`}
        isDisabled={this.isSaveDisabled}
        isSaving={this.props.isSaving}
        onSave={this.onSaveField}
      >
        <p>{this.helpText}</p>
        <CodeEditor
          errors={this.activeError}
          maxLines={20}
          minLines={20}
          onChange={this.onInputChange}
          value={this.dynamicInput}
          width="100%"
        />
      </EditorBodyLayout>
    );
  }
}

export default ActionDynamicInputEditor;
